import { HttpRequest } from './HttpRequest';
import { quotationUrl, baseUrl } from 'utils/ApiRoutes';

export const createQuotation = async (
  body: IBodyResponseCreateQuoteCars,
  onError: (error: string) => void
): Promise<IQuoteCreationResponse> => {
  try {
    const sendQuotation = await new HttpRequest().Post<IQuoteCreationResponse>(
      `${baseUrl}${quotationUrl.quotation}`,
      body
    );
    return sendQuotation.data;
  } catch (err) {
    throw onError(
      'Ha ocurrido un error al crear la cotización, por favor revisar la información e intentar de nuevo.'
    );
  }
};

export const getFasecoldaCode = async (
  licensePlate: string,
  onError: (error: string) => void
): Promise<IFasecoldaCodeResponse> => {
  try {
    const fasecolda = await new HttpRequest().Get<IFasecoldaCodeResponse>(
      `${baseUrl}${quotationUrl.fasecoldaCode}${licensePlate}`
    );
    return fasecolda.data;
  } catch (err) {
    throw onError(
      'Lo sentimos nos es imposible encontrar los datos de tu placa, intenta ingresando el fasecolda y el modelo de forma manual.'
    );
  }
};

export const getQuotationResume = async (
  typeQuoteWithId: string
): Promise<IQuotationResumeService> => {
  try {
    const resume = await new HttpRequest().Get<IQuotationResumeService>(
      `${baseUrl}${quotationUrl.quoteResume}${typeQuoteWithId}`
    );
    return resume.data;
  } catch (err) {
    throw new Error(err);
  }
};

export const getInsureStatus = async (
  uuid: string,
  insurerName: string | undefined
): Promise<IGetInsureStatusResponse> => {
  try {
    const stateInsure = await new HttpRequest().Get<IGetInsureStatusResponse>(
      `${baseUrl}${quotationUrl.stateInsurer}${uuid}/${insurerName}`
    );
    return stateInsure.data;
  } catch (err) {
    throw new Error(err);
  }
};

export const getValidateQuotation = async (
  licensePlate: string,
  onError: (error: string) => void
): Promise<IValidateQuotationResponse> => {
  try {
    const validation = await new HttpRequest().Get<IValidateQuotationResponse>(
      `${baseUrl}${quotationUrl.validateQuotation}${licensePlate}`
    );

    return validation.data;
  } catch (err) {
    throw onError(err.response.data.message);
  }
};

export const getValidateOpportunity = async (
  licensePlate: string,
  nDocument: string,
  onError: (error: string) => void
): Promise<IValidateOpportunity | null> => {
  try {
    const validation = await new HttpRequest().Get<IValidateOpportunity>(
      `${baseUrl}${quotationUrl.validateOpportunity}${licensePlate}/${nDocument}`
    );
    return validation.data;
  } catch (error) {
    const err = error as any;
    if (err.response?.status === 404) {
      onError(err.response?.data?.message || 'No se encontraron oportunidades.');
      return null;
    }

    // Un 500 suele ser un fallo puntual de la API de Salesforce, no un rechazo
    // real de la oportunidad — se deja continuar la cotización en vez de
    // bloquear al usuario por una caída del proveedor externo.
    if (err.response?.status === 500) {
      onError('api_error'); // Indicador especial para error de API
      return null;
    }

    throw onError(err.response?.data?.message || 'Error desconocido.');
  }
};

export const getRefFasecolda = async (
  fasecolda: string | undefined,
  onError: (error: string) => void
): Promise<IRefFasecoldaResponse> => {
  try {
    const refs = await new HttpRequest().Get<IRefFasecoldaResponse>(
      `${baseUrl}${quotationUrl.refFasecolda}${fasecolda}`
    );

    return refs.data;
  } catch (err) {
    throw onError(err.response.data.message);
  }
};

export const resetBotQuotation = async (
  uuid: string,
  insurer: string
): Promise<IResetBotResponse> => {
  try {
    const reset = await new HttpRequest().Get<IResetBotResponse>(
      `${baseUrl}${quotationUrl.resetBot}${uuid}/${insurer}`
    );

    return reset.data;
  } catch (err) {
    throw new Error(err);
  }
};

export const getInsureStatusClient = async (uuid: string): Promise<any> => {
  try {
    const productsInsures = await new HttpRequest().Get<any>(
      `${baseUrl}${quotationUrl.stateInsurerClient}${uuid}`
    );
    return productsInsures.data;
  } catch (err) {
    console.log(err);
    return '';
  }
};

export const getInsuranceRate = async (valueToFinanced: number): Promise<any> => {
  try {
    const productsInsures = await new HttpRequest().Get<any>(
      `${baseUrl}${quotationUrl.interestValueRate}${valueToFinanced}`
    );
    return productsInsures.data;
  } catch (err) {
    console.log(err);
    return '';
  }
};

export const getLinkOffer = async (body: Object): Promise<any> => {
  try {
    const linkOffer = await new HttpRequest().Post<any>(
      `${baseUrl}${quotationUrl.getLinkOffer}`,
      body
    );
    return linkOffer.data;
  } catch (err) {
    return null;
  }
};

export const getOffer = async (id: string): Promise<any> => {
  try {
    const offer = await new HttpRequest().GetWithoutToken<any>(
      `${baseUrl}${quotationUrl.getOffer}${id}`
    );
    return offer.data;
  } catch (error) {
    return null;
  }
};

export const getAvailableSegments = async (): Promise<any> => {
  try {
    const segments = await new HttpRequest().Get<any>(`${baseUrl}${quotationUrl.getSegments}`);
    return segments.data;
  } catch (err) {
    console.error('Error al obtener segmentos:', err);
    return null;
  }
};

export const getAvailableProducts = async (): Promise<any> => {
  try {
    const products = await new HttpRequest().Get<any>(`${baseUrl}/productos`);
    return products.data;
  } catch (err) {
    console.error('Error al obtener productos:', err);
    return null;
  }
};

export const getProductDetails = async (productKey: string): Promise<any> => {
  try {
    const productDetails = await new HttpRequest().Get<any>(
      `${baseUrl}/productos/${encodeURIComponent(productKey)}`
    );
    return productDetails.data;
  } catch (err) {
    console.error('Error al obtener detalles del producto:', err);
    return null;
  }
};
